/* PCIS — Error Code Mapping (route /supply/pcis-health/error-mapping).
 *
 * Ops-managed mapping of a courier's own error (an exact code, or a keyword/regex in its message) to a
 * PCIS standard error sub_code (PcisErrorCode). PCIS returns that standard code in the error block of
 * every failed API response and stores it on failed orders, so failures can be analysed by a stable
 * code instead of raw courier text. Unmatched errors fall back to an in-code classifier, then to
 * COURIER_ERROR (5021).
 *
 * The sub_code dropdown is PCIS's own catalogue (/error-codes) — PCIS owns this contract. All calls go
 * to ProShip /api/pcis-admin/courier-error-mappings (which proxies PCIS). Cache auto-evicts on edit and
 * is clearable under PCIS → Cache.
 */
import React, { useMemo, useState } from 'react'
import { Box, Stack, Button, Typography, IconButton, Tooltip, Chip, Alert, TextField } from '@mui/material'
import AddOutlinedIcon from '@mui/icons-material/AddOutlined'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import PageTitle from '../../../components/PageTitle'
import { PageHeader, SearchSelect, Modal, FileDrop, ConfirmDialog, TableSkeleton, LoadErrorPanel, useToast } from '../components'
import ProzoDataGrid from '../../../common/ProzoDataGrid'
import { get } from '../../../common/constants'
import { downloadExcelFile } from '../../../common/util'
import {
  usePcisErrorCodesQuery,
  usePcisErrorMappingCouriersQuery,
  usePcisErrorMappingsQuery,
  useUpsertPcisErrorMappingMutation,
  useDeletePcisErrorMappingMutation,
  useUploadPcisErrorMappingsMutation,
} from '../../../redux/supplyApiSlice'

const MATCH_TYPES = [
  { value: 'CONTAINS', label: 'CONTAINS (keyword in message)' },
  { value: 'EXACT', label: 'EXACT (courier error code)' },
  { value: 'REGEX', label: 'REGEX (message pattern)' },
]
const EMPTY_FORM = { id: null, courier_code: '', match_type: 'CONTAINS', match_value: '', sub_code: '', priority: 100, note: '' }

export default function PcisErrorMapping() {
  const toast = useToast()

  const [courier, setCourier] = useState('')
  const [page, setPage] = useState(0)
  const [sizePerPage, setSizePerPage] = useState(20)

  const [form, setForm] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [uploadOpen, setUploadOpen] = useState(false)
  const [uploadFile, setUploadFile] = useState(null)
  const [uploadResult, setUploadResult] = useState(null)
  const [downloading, setDownloading] = useState(false)

  const { data: couriers = [], isLoading: couriersLoading } = usePcisErrorMappingCouriersQuery()
  const { data: errorCodes = [] } = usePcisErrorCodesQuery()
  const {
    data: mappings = [], isFetching, isError, refetch,
  } = usePcisErrorMappingsQuery(courier, { skip: !courier })

  const [upsert, { isLoading: saving }] = useUpsertPcisErrorMappingMutation()
  const [remove, { isLoading: deleting }] = useDeletePcisErrorMappingMutation()
  const [upload, { isLoading: uploading }] = useUploadPcisErrorMappingsMutation()

  const codeOptions = useMemo(
    () => errorCodes.map((c) => ({ value: c.subCode, label: `${c.subCode} — ${c.errorType} (HTTP ${c.httpCode})` })),
    [errorCodes],
  )
  const codeLabel = useMemo(() => {
    const m = {}
    errorCodes.forEach((c) => { m[c.subCode] = `${c.errorType}` })
    return m
  }, [errorCodes])

  const columns = [
    { field: 'matchType', headerName: 'Match', width: 120, renderCell: (p) => <Chip size='small' variant='outlined' label={p.row.matchType} /> },
    { field: 'matchValue', headerName: 'Courier code / keyword / regex', flex: 1.6, minWidth: 240,
      renderCell: (p) => <code style={{ fontSize: 12 }}>{p.row.matchValue}</code> },
    { field: 'subCode', headerName: 'PCIS Code', width: 200,
      renderCell: (p) => <span><b>{p.row.subCode}</b>{codeLabel[p.row.subCode] ? ` — ${codeLabel[p.row.subCode]}` : ''}</span> },
    { field: 'priority', headerName: 'Priority', width: 90 },
    { field: 'note', headerName: 'Note', flex: 1, minWidth: 140, renderCell: (p) => p.row.note || <span style={{ opacity: 0.5 }}>—</span> },
    {
      field: 'actions', headerName: 'Actions', width: 110, sortable: false, filterable: false,
      renderCell: (p) => (
        <Stack direction='row' spacing={0.5}>
          <Tooltip title='Edit'><IconButton size='small' onClick={() => openEdit(p.row)}><EditOutlinedIcon fontSize='small' /></IconButton></Tooltip>
          <Tooltip title='Delete'><IconButton size='small' color='error' onClick={() => setDeleteTarget(p.row)}><DeleteOutlineOutlinedIcon fontSize='small' /></IconButton></Tooltip>
        </Stack>
      ),
    },
  ]

  function openAdd() { setForm({ ...EMPTY_FORM, courier_code: courier || '' }) }
  function openEdit(row) {
    setForm({
      id: row.id, courier_code: row.courierCode, match_type: row.matchType,
      match_value: row.matchValue, sub_code: row.subCode, priority: row.priority ?? 100, note: row.note || '',
    })
  }

  async function saveForm() {
    if (!form.courier_code || !String(form.courier_code).trim()) return toast('Courier code is required.', 'error')
    if (!form.match_value || !String(form.match_value).trim()) return toast('Match value is required.', 'error')
    if (form.sub_code === '' || form.sub_code === null || form.sub_code === undefined) return toast('Pick a PCIS error code.', 'error')
    const body = {
      id: form.id || undefined,
      courier_code: String(form.courier_code).trim().toUpperCase(),
      match_type: form.match_type,
      match_value: String(form.match_value).trim(),
      sub_code: Number(form.sub_code),
      priority: Number(form.priority) || 100,
      note: form.note || null,
    }
    try {
      await upsert(body).unwrap()
      toast('Rule saved.', 'success')
      setForm(null)
      if (body.courier_code === courier) refetch()
      else setCourier(body.courier_code)
    } catch (e) {
      toast(e?.message || 'Could not save rule.', 'error')
    }
  }

  async function confirmDelete() {
    try {
      await remove({ id: deleteTarget.id }).unwrap()
      toast('Rule deleted.', 'success')
      setDeleteTarget(null)
      refetch()
    } catch (e) {
      toast(e?.message || 'Could not delete rule.', 'error')
      setDeleteTarget(null)
    }
  }

  async function downloadTemplate() {
    setDownloading(true)
    try {
      const res = await get('pcis-admin/courier-error-mappings/template', {}, { responseType: 'blob' })
      downloadExcelFile(res.data, 'courier_error_mappings_template')
    } catch (e) {
      toast('Could not download the template.', 'error')
    }
    setDownloading(false)
  }

  async function doUpload() {
    if (!uploadFile) return toast('Choose an .xlsx file first.', 'error')
    try {
      const result = await upload({ file: uploadFile }).unwrap()
      setUploadResult(result)
      const failed = (result && result.validation_errors && result.validation_errors.length) || 0
      toast(`Uploaded: ${result?.sent_to_pcis ?? 0} row(s) sent${failed ? `, ${failed} rejected` : ''}.`,
        failed ? 'warning' : 'success')
      if (courier) refetch()
    } catch (e) {
      toast(e?.message || 'Upload failed.', 'error')
    }
  }

  function closeUpload() {
    setUploadOpen(false)
    setUploadFile(null)
    setUploadResult(null)
  }

  return (
    <>
      <PageTitle title='PCIS · Error Code Mapping' />
      <PageHeader
        title='Error Code Mapping'
        sub='Map a courier error (code or message keyword) to a PCIS standard code. Bulk-upload is replace-per-courier; unmatched errors fall back to COURIER_ERROR (5021).'
        actions={
          <>
            <Button size='small' variant='outlined' startIcon={<DownloadOutlinedIcon />}
              onClick={downloadTemplate} disabled={downloading}>Template</Button>
            <Button size='small' variant='outlined' startIcon={<UploadFileOutlinedIcon />}
              onClick={() => setUploadOpen(true)}>Upload Excel</Button>
            <Button size='small' variant='contained' startIcon={<AddOutlinedIcon />}
              onClick={openAdd}>Add Rule</Button>
          </>
        }
      />

      <Box sx={{ maxWidth: 360, mb: 2 }}>
        <Typography variant='caption' sx={{ opacity: 0.7 }}>Courier (with rules)</Typography>
        <SearchSelect
          value={courier}
          onChange={(v) => { setCourier(v); setPage(0) }}
          options={couriers}
          placeholder='Select a courier…'
          loading={couriersLoading}
        />
      </Box>

      {!courier ? (
        <Alert severity='info'>Select a courier to view its rules — or click <b>Add Rule</b> to create the first one for any courier.</Alert>
      ) : isFetching ? (
        <TableSkeleton />
      ) : isError ? (
        <LoadErrorPanel onRetry={refetch} />
      ) : (
        <Box sx={{ height: 600 }}>
          <ProzoDataGrid
            columns={columns}
            rows={mappings}
            getRowId={(r) => r.id}
            rowCount={mappings.length}
            paginationMode='client'
            page={page}
            setPage={setPage}
            sizePerPage={sizePerPage}
            setSizePerPage={setSizePerPage}
            pagination
          />
        </Box>
      )}

      {/* ── Add / Edit ─────────────────────────────────────────────────────── */}
      <Modal
        open={!!form}
        onClose={() => setForm(null)}
        title={form && form.id ? 'Edit Error Rule' : 'Add Error Rule'}
        width={560}
        actions={[
          { label: 'Cancel', onClick: () => setForm(null) },
          { label: saving ? 'Saving…' : 'Save', primary: true, onClick: saveForm, disabled: saving, closeOnClick: false },
        ]}
      >
        {form && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label='Courier code' size='small' fullWidth
              value={form.courier_code}
              onChange={(e) => setForm((f) => ({ ...f, courier_code: e.target.value }))}
              disabled={!!form.id}
              helperText='e.g. DELHIVERY, BLUEDART' />
            <div>
              <Typography variant='caption' sx={{ opacity: 0.7 }}>Match type</Typography>
              <SearchSelect value={form.match_type} onChange={(v) => setForm((f) => ({ ...f, match_type: v }))} options={MATCH_TYPES} />
            </div>
            <TextField label='Match value' size='small' fullWidth
              value={form.match_value}
              onChange={(e) => setForm((f) => ({ ...f, match_value: e.target.value }))}
              helperText='EXACT: the courier’s error code · CONTAINS: a keyword in the message · REGEX: a pattern' />
            <div>
              <Typography variant='caption' sx={{ opacity: 0.7 }}>PCIS error code (sub_code)</Typography>
              <SearchSelect value={form.sub_code} onChange={(v) => setForm((f) => ({ ...f, sub_code: v }))}
                options={codeOptions} placeholder='Select a PCIS error code…' />
            </div>
            <Stack direction='row' spacing={2}>
              <TextField label='Priority (lower first)' size='small' type='number' sx={{ width: 180 }}
                value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))} />
              <TextField label='Note (optional)' size='small' fullWidth
                value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} />
            </Stack>
          </Stack>
        )}
      </Modal>

      {/* ── Delete confirm ─────────────────────────────────────────────────── */}
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title='Delete error rule?'
        body={deleteTarget ? `${deleteTarget.courierCode} · ${deleteTarget.matchType} "${deleteTarget.matchValue}" → ${deleteTarget.subCode}` : ''}
        okLabel={deleting ? 'Deleting…' : 'Delete'}
        danger
        onOk={confirmDelete}
      />

      {/* ── Upload Excel ───────────────────────────────────────────────────── */}
      <Modal
        open={uploadOpen}
        onClose={closeUpload}
        title='Upload Error Rules'
        width={640}
        actions={[
          { label: 'Close', onClick: closeUpload },
          { label: uploading ? 'Uploading…' : 'Upload', primary: true, onClick: doUpload, disabled: uploading || !uploadFile, closeOnClick: false },
        ]}
      >
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant='body2' sx={{ opacity: 0.8 }}>
            Columns: <b>courier_code | match_type | match_value | sub_code | priority | note</b>.
            Applied as <b>replace-per-courier</b> — every courier in the sheet has its rules replaced by
            the sheet’s rows (re-upload is safe, no duplicates).
          </Typography>
          <FileDrop
            accept='.xlsx'
            title='Drop the .xlsx here or click to choose'
            hint='courier_code, match_type, match_value and sub_code are required'
            onFile={(f) => { setUploadFile(f); setUploadResult(null) }}
            onError={(m) => toast(m, 'error')}
          />
          {uploadFile && <Typography variant='caption'>Selected: {uploadFile.name}</Typography>}

          {uploadResult && (
            <Box>
              <Stack direction='row' spacing={1} sx={{ mb: 1 }}>
                <Chip size='small' color='success' label={`Sent: ${uploadResult.sent_to_pcis ?? 0}`} />
                {(uploadResult.validation_errors || []).length > 0 &&
                  <Chip size='small' color='error' label={`Rejected: ${uploadResult.validation_errors.length}`} />}
              </Stack>
              {(uploadResult.validation_errors || []).length > 0 && (
                <Alert severity='warning' sx={{ maxHeight: 220, overflow: 'auto' }}>
                  {uploadResult.validation_errors.slice(0, 200).map((er, i) => (
                    <div key={i}>Row {er.row}: {er.courier_code || '—'} — {er.error}</div>
                  ))}
                </Alert>
              )}
            </Box>
          )}
        </Stack>
      </Modal>
    </>
  )
}
